import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Sparkles, MessageCircle, BookOpen, TrendingUp, Loader2, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [reports, setReports] = useState(null);
  const [sessions, setSessions] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/my/reports").then((r) => setReports(r.data)).catch(() => setReports([]));
    api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-10">
        <div className="overline mb-2">Welcome back</div>
        <h1 className="text-4xl font-semibold text-brand-blue">Hi, {user.name.split(" ")[0]} 👋</h1>
        <p className="mt-2 text-brand-muted">Your family's learning journey — all in one place.</p>
      </div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Link to="/assessment" className="surface surface-hover p-6">
          <Sparkles className="text-brand-green mb-3" />
          <div className="font-heading font-semibold text-brand-blue">New assessment</div>
          <div className="text-sm text-brand-muted mt-1">For a new child or update</div>
        </Link>
        <Link to="/assistant" className="surface surface-hover p-6">
          <MessageCircle className="text-brand-orange mb-3" />
          <div className="font-heading font-semibold text-brand-blue">Ask the AI</div>
          <div className="text-sm text-brand-muted mt-1">Guidance on demand</div>
        </Link>
        <Link to="/curriculum" className="surface surface-hover p-6">
          <BookOpen className="text-brand-blue mb-3" />
          <div className="font-heading font-semibold text-brand-blue">Explore curricula</div>
          <div className="text-sm text-brand-muted mt-1">18+ approaches</div>
        </Link>
        <Link to="/careers" className="surface surface-hover p-6">
          <TrendingUp className="text-brand-green mb-3" />
          <div className="font-heading font-semibold text-brand-blue">Future careers</div>
          <div className="text-sm text-brand-muted mt-1">2045-ready paths</div>
        </Link>
      </div>

      {/* My reports */}
      <div className="surface p-7 mb-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-2xl font-semibold text-brand-blue">Your reports</h2>
          <Link to="/assessment" className="text-brand-green font-medium text-sm flex items-center gap-1">New report <ArrowRight size={14} /></Link>
        </div>
        {reports === null ? <Loader2 className="animate-spin text-brand-green" /> : reports.length === 0 ? (
          <div className="text-center py-8 text-brand-muted">
            No reports yet. <Link to="/assessment" className="text-brand-green underline">Take the assessment</Link>.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {reports.map((r) => (
              <Link key={r.id} to={`/report/${r.id}`} data-testid={`dashboard-report-${r.id}`} className="p-5 rounded-2xl bg-brand-bg border border-brand-border hover:border-brand-blue transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading font-semibold text-brand-blue">{r.input?.child_name || "Report"}, {r.input?.child_age || "?"}</div>
                  <span className="chip">{r.suitability_score}/100</span>
                </div>
                <div className="text-xs text-brand-muted line-clamp-2">{r.summary}</div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Chat sessions */}
      <div className="surface p-7">
        <h2 className="text-2xl font-semibold text-brand-blue mb-5">Recent chats</h2>
        {sessions === null ? <Loader2 className="animate-spin text-brand-green" /> : sessions.length === 0 ? (
          <div className="text-center py-8 text-brand-muted">
            No chats yet. <Link to="/assistant" className="text-brand-green underline">Ask the AI</Link>.
          </div>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li key={s.id} className="p-4 rounded-xl bg-brand-bg border border-brand-border flex justify-between items-center">
                <div>
                  <div className="font-medium text-brand-blue">{s.title}</div>
                  <div className="text-xs text-brand-muted">{new Date(s.created_at).toLocaleDateString()}</div>
                </div>
                <span className="text-xs text-brand-muted">{s.messages?.length || 0} msgs</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
